
import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { updateCompanyMutation, CompanyUpdateInput } from '@/graphql/mutations/updateCompany';
import { setCompany } from '@/store/companySlice';

interface Company {
  slug: string;
  logoUrl: string | null;
  category: string;
  employees: string;
  description: string | null;
  url: string | null;
  legacySlug: string | null;
  pages: any[];
  pricingMetrics: any[]; // Required by Redux store
  scraper?: string;
}

interface EditCompanyModalProps {
  company: Company;
  isOpen: boolean;
  onClose: () => void;
  onCompanyUpdated: (updatedCompany: Company) => void;
}

const EditCompanyModal = ({ company, isOpen, onClose, onCompanyUpdated }: EditCompanyModalProps) => {
  const [category, setCategory] = useState(company.category || '');
  const [employees, setEmployees] = useState(company.employees || '');
  const [description, setDescription] = useState(company.description || '');
  // Map empty string to 'default' for UI display
  const [scraper, setScraper] = useState(company.scraper === '' || !company.scraper ? 'default' : company.scraper);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Get categories and employees from Redux store
  const uniqueCategories = useAppSelector(state => 
    state.supaCompanies.uniqueCategories.filter(cat => cat !== 'All')
  );
  const uniqueEmployees = useAppSelector(state => 
    state.supaCompanies.uniqueEmployees.filter(emp => emp !== 'All')
  );

  // Reset state when modal opens with new company
  React.useEffect(() => {
    if (isOpen) {
      setCategory(company.category || '');
      setEmployees(company.employees || '');
      setDescription(company.description || '');
      // Map empty string to 'default' for UI display
      setScraper(company.scraper === '' || !company.scraper ? 'default' : company.scraper);
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, company]);

  const dispatch = useAppDispatch();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Prepare input data for the mutation
      const input: CompanyUpdateInput = {
        category,
        employees,
        description,
        scraper: scraper === 'default' ? '' : scraper // Convert 'default' to empty string for the GraphQL API
      };

      console.log('Submitting company update:', { slug: company.slug, input });
      
      // Update the company using GraphQL mutation
      const response = await updateCompanyMutation(company.slug, input);
      
      // Log the response for debugging - this is important to see the exact structure
      console.log('GraphQL update response:', JSON.stringify(response, null, 2));
      
      // Determine how to extract the company data based on the response structure
      let updatedCompanyData;
      
      if (response?.updateCompany) {
        // Standard response structure
        updatedCompanyData = response.updateCompany;
        console.log('Found expected response structure with updateCompany property');
      } else if (response && typeof response === 'object') {
        // Maybe the SDK is returning the data directly without the operation name wrapper
        updatedCompanyData = response;
        console.log('Using direct response as company data');
      } else {
        console.error('Invalid response structure:', response);
        throw new Error('Invalid response format from GraphQL server');
      }
      
      // Verify we have the needed properties
      if (!updatedCompanyData || typeof updatedCompanyData !== 'object') {
        throw new Error('Company data is missing or invalid');
      }
      
      // Log the extracted company data
      console.log('Extracted company data:', updatedCompanyData);

      // Create an updated company object with all the original properties
      const updatedCompany = {
        ...company,
        // Use the updatedCompanyData if available, otherwise keep the original values
        category: updatedCompanyData.category || company.category,
        employees: updatedCompanyData.employees || company.employees,
        description: updatedCompanyData.description ?? company.description,
        scraper: (updatedCompanyData.scraper !== undefined) ? updatedCompanyData.scraper : company.scraper || '',
        // Ensure pricingMetrics is included to match Redux state requirements
        pricingMetrics: company.pricingMetrics || []
      };

      // Update Redux state
      dispatch(setCompany(updatedCompany));

      // Call the callback if provided
      if (onCompanyUpdated) {
        onCompanyUpdated(updatedCompany);
      }
      
      // Show success message
      setSuccessMessage("Company updated successfully");

      toast({
        title: "Company Updated",
        description: `${company.slug} has been updated successfully.`,
        variant: "default",
      });

      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error: any) {
      console.error('❌ [EditCompanyModal] Error:', error);
      
      // Extract the most useful error message
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.errors && error.response.errors.length > 0) {
        // Extract GraphQL errors (from original GraphQL client format)
        errorMessage = error.response.errors.map(e => e.message).join('; ');
        console.log('GraphQL error details:', error.response.errors);
      } else if (error.error) {
        // Extract Supabase error message
        errorMessage = `Database error: ${error.error.message || 'Unknown database error'}`;
        console.log('Supabase error details:', error.error);
      } else if (error.message && error.message.includes('column')) {
        // Handle specific database schema errors
        errorMessage = `Database schema error: ${error.message}`;
      } else if (error.message) {
        // Use the standard error message
        errorMessage = error.message;
      }
      
      // Show user-friendly error message
      setError(`Error updating company: ${errorMessage}`);
      toast({
        title: "Update Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Company</DialogTitle>
        </DialogHeader>
        
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        
        {successMessage ? (
          <Alert variant="default" className="mb-4 bg-green-50 text-green-800 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        ) : null}

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="slug" className="text-right">
                Slug
              </Label>
              <Input
                id="slug"
                value={company.slug}
                className="col-span-3"
                disabled
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category" className="text-right">
                Category
              </Label>
              <Select
                value={category}
                onValueChange={setCategory}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="employees" className="text-right">
                Employees
              </Label>
              <Select
                value={employees}
                onValueChange={setEmployees}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select employee range" />
                </SelectTrigger>
                <SelectContent>
                  {uniqueEmployees.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="scraper" className="text-right">
                Screenshot Method
              </Label>
              <Select
                value={scraper}
                onValueChange={setScraper}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select screenshot method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="scrapingBee">ScrapingBee</SelectItem>
                  <SelectItem value="firecrawl">Firecrawl</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCompanyModal;
